'use client';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { BlocksContent, BlocksRenderer } from '@strapi/blocks-react-renderer';
import Link from 'next/link';
import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import theme from '@/theme';

// === List Nesting Context ===================================================
/**
 * Context to track list nesting depth for alphanumeric list style rendering
 * Depth: 0 = decimal (1, 2, 3), 1 = lowercase alpha (a, b, c), 2 = lowercase roman (i, ii, iii)
 */
const ListNestingContext = createContext<number>(0);

const useListNestingDepth = () => useContext(ListNestingContext);

// === List Style Helper Functions ============================================
/**
 * Get CSS list-style-type based on nesting depth
 * 0 = decimal, 1 = lower-alpha, 2 = lower-roman, 3+ = decimal
 */
const getListStyleType = (depth: number): string => {
  switch (depth % 3) {
    case 0:
      return 'decimal';
    case 1:
      return 'lower-alpha';
    case 2:
      return 'lower-roman';
    default:
      return 'decimal';
  }
};

// === Tick / Cross / Custom Class Indicators =================================
const TICK_INDICATOR = '(/)';
const CROSS_INDICATOR = '(x)';

const CUSTOM_CLASS_MAP = {
  mb: ['mb-0', 'mb-xs', 'mb-sm', 'mb-md', 'mb-lg', 'mb-xl'],
  mt: ['mt-0', 'mt-xs', 'mt-sm', 'mt-md', 'mt-lg', 'mt-xl'],
  ta: ['ta-center', 'ta-left', 'ta-right'],
  fs: [
    'fs-tiny',
    'fs-small',
    'fs-medium',
    'fs-large',
    'fs-h1',
    'fs-h2',
    'fs-h3',
    'fs-h4',
    'fs-h5',
    'fs-h6',
  ],
};

/**
 * Extracts and filters custom classes from indicator text at the start
 * Only keeps the first class from each category (mb, mt, ta, fs)
 * Supports both (/class1/class2/) and (/class1/class2) formats
 */
const extractCustomClasses = (text: string): string[] => {
  const match = text.match(/^\(\/([^)]+)\/?\)/);
  if (!match) return [];

  const classesString = match[1];
  const classes = classesString.split('/').filter(Boolean);

  const categorySeen = new Set<string>();
  const filteredClasses: string[] = [];

  for (const className of classes) {
    let category: string | null = null;
    for (const [cat, validClasses] of Object.entries(CUSTOM_CLASS_MAP)) {
      if (validClasses.includes(className)) {
        category = cat;
        break;
      }
    }
    if (category && !categorySeen.has(category)) {
      categorySeen.add(category);
      filteredClasses.push(className);
    }
  }

  return filteredClasses;
};

const removeCustomClassIndicators = (text: string): string =>
  text.replace(/^\(\/([^)]+)\/?\)/, '').trimStart();

const hasCustomClassIndicator = (text: string): boolean => /^\(\/([^)]+)\/?\)/.test(text);

export const prefixClasses = (classes: string[]): string =>
  classes.map((className) => `cms-${className}`).join(' ');

/**
 * Same custom class indicator support as processCustomClasses, for plain text
 * fields (e.g. short-text Title props) rather than rich text block children.
 */
export const processCustomClassesFromText = (
  text: string | null | undefined
): { classes: string[]; cleanedText: string } => {
  if (!text || !hasCustomClassIndicator(text)) {
    return { classes: [], cleanedText: text ?? '' };
  }

  return { classes: extractCustomClasses(text), cleanedText: removeCustomClassIndicators(text) };
};

/**
 * Helper to extract classes and clean text from children
 * Handles custom class indicators only (tick is handled separately)
 */
const processCustomClasses = (
  children: ReactNode[] | undefined
): { classes: string[]; cleanedChildren: ReactNode[] } => {
  if (!children || children.length === 0) {
    return { classes: [], cleanedChildren: children || [] };
  }

  const firstChild = children[0] as ReactElement;
  if (!firstChild) {
    return { classes: [], cleanedChildren: children };
  }

  const text = firstChild.props?.text as string | undefined;
  if (!text) {
    return { classes: [], cleanedChildren: children };
  }

  const classes = extractCustomClasses(text);

  if (!hasCustomClassIndicator(text)) {
    return { classes: [], cleanedChildren: children };
  }

  const processedText = removeCustomClassIndicators(text);

  if (!processedText) {
    return { classes, cleanedChildren: children.slice(1) };
  }

  const cleanedChildren = [
    {
      ...firstChild,
      props: { ...firstChild.props, text: processedText },
    },
    ...children.slice(1),
  ];

  return { classes, cleanedChildren };
};

// === Shared Overrides =======================================================
export const sharedOverrides = {
  boldPill: {
    bold: ({ children }: { children?: ReactNode }) => (
      <>
        <strong>{children}</strong>&nbsp;
      </>
    ),
  },

  simpleLink: {
    link: ({ url, children }: { url: string; children?: ReactNode }) => (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
  },

  headingCenter: {
    heading: ({ children, level }: { children?: ReactNode; level: number }) => {
      const variant = level === 1 ? 'h2' : 'h6';
      return (
        <Typography
          variant={variant}
          sx={{
            textAlign: 'center',
            mb: theme.spacing(4),
          }}
        >
          {children}
        </Typography>
      );
    },
  },

  headingSimple: {
    heading: ({ children, level }: { children?: ReactNode; level: number }) => {
      const variant = level === 1 ? 'h1' : 'h6';
      return (
        <Typography variant={variant} sx={{ mb: theme.spacing(3) }}>
          {children}
        </Typography>
      );
    },
  },

  paragraphSimple: {
    paragraph: ({ children }: { children?: ReactNode }) => (
      <Typography
        variant="body1"
        sx={{
          mb: theme.spacing(2),
          lineHeight: 1.75,
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
        }}
      >
        {children}
      </Typography>
    ),
  },

  paragraphBody: {
    paragraph: ({ children }: { children?: ReactNode }) => (
      <Typography
        variant="body1"
        sx={{
          mb: theme.spacing(3),
          lineHeight: 1.75,
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
        }}
      >
        {children}
      </Typography>
    ),
  },
};

// === Headings ===============================================================
export const GlobalHeadingRenderer =
  ({
    forceHeadingLevel,
    textColor,
    center = false,
  }: {
    textColor?: string;
    forceHeadingLevel?: number;
    center?: boolean;
  }) =>
  // eslint-disable-next-line react/display-name
  ({ children, level }: { children?: ReactNode; level: number }) => {
    const headingLevel = (forceHeadingLevel ?? level) as 1 | 2 | 3 | 4 | 5 | 6;
    const variantMap = {
      1: 'h1',
      2: 'h2',
      3: 'h3',
      4: 'h4',
      5: 'h5',
      6: 'h6',
    } as const;

    // Check for custom class indicator
    const childrenArray = Array.isArray(children) ? children : children ? [children] : [];
    const { classes, cleanedChildren } = processCustomClasses(childrenArray);
    const finalChildren = cleanedChildren.length === 1 ? cleanedChildren[0] : cleanedChildren;

    return (
      <Typography
        variant={variantMap[headingLevel] ?? 'h6'}
        component={`h${headingLevel}` as keyof JSX.IntrinsicElements}
        className={classes.length > 0 ? prefixClasses(classes) : undefined}
        sx={(theme) => ({
          mt: theme.spacing(headingLevel > 3 ? 4 : 2),
          mb: theme.spacing(headingLevel > 3 ? 4 : 3),
          color: textColor ?? 'inherit',
          textAlign: center ? 'center' : 'left',
          fontWeight: 600,
          lineHeight: 1.3,
        })}
      >
        {finalChildren}
      </Typography>
    );
  };

// === Paragraph ==============================================================
const READ_MORE_INDICATOR = '(/READ_MORE)';

const GlobalParagraphRenderer =
  ({ textColor }: { textColor?: string }) =>
  // eslint-disable-next-line react/display-name
  ({ children }: { children?: ReactNode }) => {
    const childrenArray = Array.isArray(children) ? children : children ? [children] : [];

    const { classes, cleanedChildren } = processCustomClasses(childrenArray);

    const tokenRegex = /(\(\/\)|\(x\))/g;

    const tickSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 -2 26 24">
        <path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" fill='${theme.palette.brand.green}'/>
      </svg>`
    )}`;

    const crossSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 -2 26 24">
        <path d="M12 2a10 10 0 1 1 0 20a10 10 0 0 1 0 -20zm3.536 5.464a1 1 0 0 0 -1.414 0L12 9.586L9.879 7.464a1 1 0 1 0 -1.414 1.414L10.586 11l-2.121 2.121a1 1 0 1 0 1.414 1.414L12 12.414l2.121 2.121a1 1 0 0 0 1.414 -1.414L13.414 11l2.121 -2.121a1 1 0 0 0 0 -1.414z" fill='${theme.palette.error.main}'/>
      </svg>`
    )}`;

    const iconSpan = (kind: 'tick' | 'cross', key: string) => (
      <Box
        key={key}
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.15em',
          height: '1.15em',
          ml: 0.5,
          mr: 0.1,
          verticalAlign: 'sub',
          backgroundImage: `url("${kind === 'tick' ? tickSvg : crossSvg}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          flex: '0 0 auto',
        }}
      />
    );

    const expandedChildren: ReactNode[] = [];
    let trimNextStart = false;

    const trimTextNodeEndIfPossible = () => {
      const last = expandedChildren[expandedChildren.length - 1] as ReactElement | undefined;
      const lastText = (last?.props?.text as string | undefined) ?? '';
      if (!lastText) return;

      const trimmed = lastText.replace(/\s+$/, '');
      if (trimmed !== lastText) {
        expandedChildren[expandedChildren.length - 1] = {
          ...last,
          props: { ...(last?.props ?? {}), text: trimmed },
        } as ReactElement;
      }
    };

    cleanedChildren.forEach((child, childIndex) => {
      const el = child as ReactElement | undefined;
      const text = (el?.props?.text as string | undefined) ?? '';

      // Non-text node -> keep
      if (!text) {
        expandedChildren.push(child);
        return;
      }

      // If no tokens, just push (with optional trimStart)
      if (!tokenRegex.test(text)) {
        tokenRegex.lastIndex = 0;

        const nextText = trimNextStart ? text.replace(/^\s+/, '') : text;
        trimNextStart = false;

        expandedChildren.push({
          ...el,
          key: el?.key ?? `p-${childIndex}-txt`,
          props: { ...el?.props, text: nextText },
        } as ReactElement);

        return;
      }

      tokenRegex.lastIndex = 0;
      const parts = text.split(tokenRegex);

      parts.forEach((part, partIndex) => {
        if (part == null || part === '') return;

        if (part === TICK_INDICATOR || part === CROSS_INDICATOR) {
          trimTextNodeEndIfPossible();
          trimNextStart = true;

          expandedChildren.push(
            iconSpan(
              part === TICK_INDICATOR ? 'tick' : 'cross',
              `p-${childIndex}-${partIndex}-${part === TICK_INDICATOR ? 't' : 'c'}`
            )
          );
          return;
        }

        const nextText = trimNextStart ? part.replace(/^\s+/, '') : part;
        trimNextStart = false;

        expandedChildren.push({
          ...el,
          key: el?.key ?? `p-${childIndex}-${partIndex}-txt`,
          props: { ...el?.props, text: nextText },
        } as ReactElement);
      });
    });

    const finalChildren = expandedChildren.length === 1 ? expandedChildren[0] : expandedChildren;

    return (
      <Typography
        variant="body1"
        className={classes.length > 0 ? prefixClasses(classes) : undefined}
        sx={(theme) => ({
          my: theme.spacing(2),
          lineHeight: 1.75,
          color: textColor ?? 'inherit',
          fontSize: 'clamp(1.05rem, 0.45vw + 0.7rem, 1.25rem)',
        })}
      >
        {finalChildren}
      </Typography>
    );
  };

// === Lists ==================================================================
const GlobalListRenderer = ({
  textColor,
  backgroundColor,
}: {
  textColor?: string;
  backgroundColor?: string;
}) => {
  const ListRenderer = ({ children, format }: { children?: ReactNode; format: string }) => {
    const depth = useListNestingDepth();

    // Check for custom class indicator in first list item
    const childrenArray = Array.isArray(children) ? children : [children];
    const firstListItem = childrenArray[0] as ReactElement | undefined;
    const firstListItemChildren = firstListItem?.props?.children;
    const firstListItemChildrenArray = Array.isArray(firstListItemChildren)
      ? firstListItemChildren
      : [firstListItemChildren];

    const { classes, cleanedChildren: cleanedListItemChildren } = processCustomClasses(
      firstListItemChildrenArray
    );

    // If we found classes, update the first list item
    const processedChildren =
      classes.length > 0 && firstListItem
        ? [
            {
              ...firstListItem,
              props: {
                ...firstListItem.props,
                children: cleanedListItemChildren,
              },
            },
            ...childrenArray.slice(1),
          ]
        : childrenArray;

    const sharedSx = (theme: Theme) => ({
      pl: theme.spacing(2),
      my: theme.spacing(2),
      color: textColor ?? 'inherit',
      fontSize: 'inherit',
      lineHeight: 1.75,
      ...(backgroundColor && {
        backgroundColor,
        borderRadius: theme.shape.borderRadius,
        p: theme.spacing(1.5, 2),
      }),
    });

    // Determine list style based on nesting depth for ordered lists
    const listStyleType = format === 'ordered' ? getListStyleType(depth) : 'disc';

    return format === 'ordered' ? (
      <Box
        component="ol"
        className={classes.length > 0 ? prefixClasses(classes) : undefined}
        sx={{ ...sharedSx, listStyleType }}
      >
        <ListNestingContext.Provider value={depth + 1}>
          {processedChildren}
        </ListNestingContext.Provider>
      </Box>
    ) : (
      <Box
        component="ul"
        className={classes.length > 0 ? prefixClasses(classes) : undefined}
        sx={{ ...sharedSx, listStyleType }}
      >
        <ListNestingContext.Provider value={depth + 1}>
          {processedChildren}
        </ListNestingContext.Provider>
      </Box>
    );
  };
  // eslint-disable-next-line react/display-name
  ListRenderer.displayName = 'GlobalListRenderer';
  return ListRenderer;
};

// === List Items =============================================================

const GlobalListItemRenderer =
  ({ textColor }: { textColor?: string }) =>
  // eslint-disable-next-line react/display-name
  ({ children }: { children?: ReactNode[] }) => {
    const firstChild = children?.[0] as ReactElement;
    const text = firstChild?.props?.text ?? '';
    const isTick = text.startsWith(TICK_INDICATOR);
    const isCross = text.startsWith(CROSS_INDICATOR);

    // Remove tick indicator from start if present, then process custom classes
    let childrenToProcess = children;

    if ((isTick || isCross) && firstChild) {
      const indicatorLength = isTick ? TICK_INDICATOR.length : CROSS_INDICATOR.length;

      const cleanedText = text.slice(indicatorLength).trim();

      childrenToProcess = cleanedText
        ? [
            {
              ...firstChild,
              props: {
                ...firstChild.props,
                text: cleanedText,
              },
            },
            ...(children?.slice(1) || []),
          ]
        : children?.slice(1) || [];
    }

    // Check for custom class indicator
    const { classes, cleanedChildren: processedChildren } = processCustomClasses(childrenToProcess);

    return (
      <Box
        component="li"
        className={classes.length > 0 ? prefixClasses(classes) : undefined}
        sx={(theme) => ({
          mb: theme.spacing(1.5),
          ml: theme.spacing(2),
          pl: theme.spacing(2),
          fontSize: 'inherit',
          '&::marker':
            isTick || isCross
              ? {
                  content: `url("data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    isTick
                      ? `
          <svg xmlns='http://www.w3.org/2000/svg' height='1.25rem' width='1.25rem' viewBox='2 -2 26 24'>
            <path d='M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z'
              fill='${theme.palette.brand.green}'/>
          </svg>`
                      : `
          <svg xmlns='http://www.w3.org/2000/svg' height='1.25rem' width='1.25rem' viewBox='2 -2 26 24'>
            <path d='M12 2a10 10 0 1 1 0 20a10 10 0 0 1 0 -20zm3.536 5.464a1 1 0 0 0 -1.414 0L12 9.586L9.879 7.464a1 1 0 1 0 -1.414 1.414L10.586 11l-2.121 2.121a1 1 0 1 0 1.414 1.414L12 12.414l2.121 2.121a1 1 0 0 0 1.414 -1.414L13.414 11l2.121 -2.121a1 1 0 0 0 0 -1.414z'
              fill='${theme.palette.error.main}'/>
          </svg>`
                  )}")`,
                }
              : {
                  fontSize: '1em',
                },
        })}
      >
        <Typography
          variant="body1"
          component="span"
          sx={{
            fontSize: 'inherit',
            lineHeight: 1.75,
            color: textColor ?? 'inherit',
            position: 'relative',
            top: isTick || isCross ? '-0.2rem' : 0,
          }}
        >
          {processedChildren}
        </Typography>
      </Box>
    );
  };

// === Modifiers ==============================================================
const GlobalBoldModifier = ({ children }: { children?: ReactNode }) => (
  <>
    <Box
      component="strong"
      sx={{
        'a > &': {
          display: 'inline-block',
          px: 2,
          py: 0.5,
          mx: 0.5,
          borderRadius: 2,
          fontSize: '1em',
          fontWeight: 500,
          lineHeight: 1.5,
          background: theme.palette.gradients.gradientCta,
          color: theme.palette.common.white,
          textDecoration: 'none !important',
          transition: 'background 0.3s ease',
          '&:hover': {
            background: theme.palette.gradients.gradient1,
          },
        },
      }}
    >
      {children}
    </Box>
    &nbsp;
  </>
);

const GlobalItalicModifier = ({ children }: { children?: ReactNode }) => <em>{children}</em>;

const GlobalUnderlineModifier = ({ children }: { children?: ReactNode }) => <u>{children}</u>;

// === Main Renderer ==========================================================
interface RenderProps {
  content: BlocksContent;
  textColor?: string;
  listBgColor?: string;
  forceHeadingLevel?: number;
  overrides?: {
    blocks?: Record<string, any>;
    modifiers?: Record<string, any>;
  };
}

const MainRenderer = ({
  content,
  textColor,
  listBgColor,
  forceHeadingLevel,
  overrides = { blocks: {}, modifiers: {} },
}: RenderProps) => {
  return (
    <ListNestingContext.Provider value={0}>
      <BlocksRenderer
        content={content}
        blocks={{
          heading: GlobalHeadingRenderer({ forceHeadingLevel, textColor }),
          paragraph: GlobalParagraphRenderer({ textColor }),
          list: GlobalListRenderer({ textColor, backgroundColor: listBgColor }),
          // @ts-expect-error does work
          'list-item': GlobalListItemRenderer({ textColor }),
          link: ({ children, url }) => (
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'underline',
                color: textColor ?? theme.palette.purple[800],
                fontWeight: 500,
              }}
            >
              {children}
            </Link>
          ),
          // Strip /uploads/ for media at bucket root; default BlocksRenderer uses raw image.url
          image: ({ image }: { image: { url: string; alternativeText?: string | null } }) => {
            const src = getImageUrl({
              image: image as Parameters<typeof getImageUrl>[0]['image'],
              size: 'url',
            });
            return src ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={src}
                alt={image.alternativeText ?? ''}
                loading="lazy"
                decoding="async"
                style={{ maxWidth: '100%', height: 'auto', display: 'inline-block' }}
              />
            ) : null;
          },
          ...overrides.blocks,
        }}
        modifiers={{
          bold: GlobalBoldModifier,
          italic: GlobalItalicModifier,
          underline: GlobalUnderlineModifier,
          ...overrides.modifiers,
        }}
      />
    </ListNestingContext.Provider>
  );
};

export default function GlobalBlockRenderer({
  content,
  textColor,
  listBgColor,
  forceHeadingLevel,
  overrides = { blocks: {}, modifiers: {} },
}: RenderProps) {
  const hasReadMore = useMemo(
    () => JSON.stringify(content).includes(READ_MORE_INDICATOR),
    [content]
  );
  const splitContent = useMemo(() => {
    if (!hasReadMore) {
      return null;
    }
    const splitIndex = content.findIndex(
      ({ type, children }) =>
        type === 'paragraph' &&
        ((children?.[0] as { text?: string })?.text ?? '').startsWith(READ_MORE_INDICATOR)
    );
    return [content.slice(0, splitIndex), content.slice(splitIndex + 1)];
  }, [content, hasReadMore]);
  if (hasReadMore && splitContent) {
    return (
      // @ts-expect-error dataComponent is valid
      <Box
        component="details"
        dataComponent="ComponentReadMore"
        sx={{
          ' summary': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            '.read-more': {
              textDecoration: 'underline',
              '.less': {
                display: 'none',
              },
            },
          },
          ' summary::marker': {
            display: 'none',
            listStyle: 'none',
          },
          '&:open summary': {
            '.read-more': {
              textDecoration: 'underline',
              ' .more': {
                display: 'none',
              },
              ' .less': {
                display: 'inline-block',
              },
            },
          },
        }}
      >
        <Box component="summary">
          {splitContent?.[0] && (
            <MainRenderer
              {...{
                content: splitContent[0],
                textColor,
                listBgColor,
                forceHeadingLevel,
                overrides,
              }}
            />
          )}
          <Typography variant="small" className="read-more">
            <span className="more">Read more</span>
            <span className="less">Read less</span>
          </Typography>
        </Box>
        {splitContent?.[1] && (
          <MainRenderer
            {...{
              content: splitContent[1],
              textColor,
              listBgColor,
              forceHeadingLevel,
              overrides,
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <MainRenderer
      {...{
        content,
        textColor,
        listBgColor,
        forceHeadingLevel,
        overrides,
      }}
    />
  );
}
